import { Badge, Box, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { SubscriptionEvent, formatDate } from "@/types/breeze/AnythinkPay";

const EVENT_LABELS: Record<string, string> = {
  created: "Created",
  plan_changed: "Plan switched",
  renewed: "Renewed",
  cancelled: "Cancelled",
  resumed: "Resumed",
  expired: "Expired",
  refunded: "Refunded",
  revoked: "Revoked",
  grace_period: "Billing grace period",
  renewal_failed: "Renewal failed",
  status_changed: "Status changed",
  force_expired: "Force-expired",
  superseded: "Superseded",
  offer_redeemed: "Offer redeemed",
};

const SOURCE_COLORS: Record<
  string,
  "gray" | "indigo" | "blue" | "amber" | "red" | "green"
> = {
  verify: "indigo",
  assn: "blue",
  user: "green",
  admin: "amber",
  system: "gray",
};

function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

export function SubscriptionHistoryCard({
  events,
}: {
  events: SubscriptionEvent[];
}) {
  return (
    <Card size="3">
      <Box p="1">
        <Heading size="4" weight="medium" mb="1">
          History
        </Heading>
        <Text size="2" color="gray" as="p" mb="4">
          Lifecycle events for this subscription, newest first.
        </Text>

        {events.length === 0 ? (
          <Text size="2" color="gray">
            No events recorded yet. New activity (renewals, cancellations, plan
            switches, refunds) will appear here.
          </Text>
        ) : (
          <Flex direction="column" gap="3">
            {events.map((ev) => (
              <Flex
                key={ev.id}
                justify="between"
                align="start"
                gap="3"
                style={{
                  borderLeft: "2px solid var(--gray-5)",
                  paddingLeft: "12px",
                }}
              >
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Flex align="center" gap="2">
                    <Text size="2" weight="medium">
                      {eventLabel(ev.event_type)}
                    </Text>
                    <Badge
                      color={SOURCE_COLORS[ev.source] ?? "gray"}
                      variant="soft"
                      size="1"
                    >
                      {ev.source}
                    </Badge>
                    {ev.prior_status && ev.new_status && (
                      <Text size="1" color="gray">
                        {ev.prior_status} → {ev.new_status}
                      </Text>
                    )}
                  </Flex>
                  {ev.summary && (
                    <Text size="2" color="gray">
                      {ev.summary}
                    </Text>
                  )}
                </Flex>
                <Text size="1" color="gray" style={{ whiteSpace: "nowrap" }}>
                  {formatDate(ev.occurred_at)}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Box>
    </Card>
  );
}
